import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ConnectError } from '@connectrpc/connect';
import { SpecraSpinner } from '@mono/ui-components/ensigns';
import { SpecraEmptyState, SpecraInlineBanner } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../../../../hooks/useProjects';
import { OfficeWorkspace } from '../../../OfficeWorkspace/OfficeWorkspace';
import { SourcesRail } from '../../../OfficeWorkspace/SourcesRail';
import { useResponseWorkspace } from '../../../OfficeWorkspace/useOfficeWorkspace';
import { ProposalSectionHead } from './ProposalSectionHead';
import { ProposalVolumeOutline, sectionHref } from './ProposalVolumeOutline';
import { pageFooter, useMarkSectionReady, useNextReviewName, usePutHeadingBack, useVolumeSections } from './useVolumeSections';
function errorText(err, fallback) {
    return err instanceof ConnectError && err.rawMessage ? err.rawMessage : fallback;
}
/**
 * A proposal volume on the writing workspace (WRT-17): the volume's Word
 * document in ONLYOFFICE, a section outline with status badges and the page
 * budget, a section head, and "Mark ready for {next review}".
 */ export function ProposalVolumeWorkspace({ documentId, projectId, sectionId, volume }) {
    const { volumeId, displayName } = volume;
    const navigate = useNavigate();
    const container = {
        kind: 'volume',
        projectId,
        volumeId
    };
    const workspace = useResponseWorkspace(container, documentId);
    const sections = useVolumeSections(projectId, volumeId, documentId);
    const nextReview = useNextReviewName(projectId);
    const project = useProject(projectId);
    const mark = useMarkSectionReady(projectId, volumeId);
    const putBack = usePutHeadingBack(projectId, volumeId);
    if (workspace.isLoading || sections.isLoading) {
        return /*#__PURE__*/ _jsx(SpecraSpinner, {
            className: "m-[var(--space-6)]",
            label: "Loading the volume"
        });
    }
    if (workspace.isError || sections.isError) {
        return /*#__PURE__*/ _jsx(SpecraEmptyState, {
            className: "m-[var(--space-6)]",
            description: "This volume's document or outline didn't load. Reload the page to try again.",
            framed: true,
            title: "Couldn't open the volume"
        });
    }
    const caps = workspace.data?.capabilities;
    const canEdit = (caps?.canEdit ?? false) && (caps?.writingOn ?? false);
    const active = sections.live.find((s)=>s.node.sectionId === sectionId);
    const removedActive = sections.removed.find((s)=>s.node.sectionId === sectionId);
    const doc = workspace.data?.documents.find((d)=>d.documentId === documentId);
    const readyCount = sections.live.filter((s)=>s.status === 'ready').length;
    const people = (project.data?.members ?? []).map((m)=>({
            userId: m.userId,
            name: m.name,
            email: m.email
        }));
    const label = nextReview ? `Mark ready for ${nextReview}` : 'Mark ready for review';
    const header = /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-[var(--space-3)]",
        children: [
            /*#__PURE__*/ _jsx(ProposalSectionHead, {
                canEdit: canEdit,
                n: active?.n ?? 0,
                nextReviewName: nextReview,
                people: people,
                projectId: projectId,
                readyCount: readyCount,
                section: active?.node ?? removedActive?.node,
                sectionCount: sections.live.length,
                volumeName: displayName
            }),
            removedActive ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "danger",
                children: "The heading for this section was removed from the document. Use Put it back in the outline to restore it."
            }) : null,
            mark.isError ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "danger",
                children: errorText(mark.error, 'The section could not be marked ready. Nothing was changed.')
            }) : null,
            putBack.isError ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "danger",
                children: errorText(putBack.error, 'The heading could not be put back. Nothing was changed.')
            }) : null
        ]
    });
    const alreadyReady = active?.status === 'ready';
    const cta = active && canEdit ? {
        label,
        testId: Testids.officeWorkspace.markReady,
        disabled: alreadyReady || mark.isPending,
        disabledReason: alreadyReady ? 'Already marked ready' : undefined,
        onClick: ()=>mark.mutate({
                sectionId: active.node.sectionId,
                baseVersionId: doc?.currentVersionId ?? '',
                label: nextReview ?? ''
            })
    } : null;
    return /*#__PURE__*/ _jsx("div", {
        className: "h-full",
        children: /*#__PURE__*/ _jsx(OfficeWorkspace, {
            anchor: active?.node.anchorBookmark || undefined,
            container: container,
            cta: cta,
            documentId: documentId,
            header: header,
            outline: /*#__PURE__*/ _jsx(ProposalVolumeOutline, {
                activeSectionId: sectionId,
                canEdit: canEdit,
                footer: pageFooter(workspace.data?.budget),
                live: sections.live,
                onPutBack: (id)=>putBack.mutate(id),
                projectId: projectId,
                puttingBack: putBack.isPending ? putBack.variables : undefined,
                removed: sections.removed,
                title: displayName,
                volumeId: volumeId
            }),
            sideTabs: {
                sources: ({ activeDocumentId, currentVersionId, remount })=>/*#__PURE__*/ _jsx(SourcesRail, {
                        baseVersionId: currentVersionId,
                        canWrite: canEdit,
                        container: container,
                        documentId: activeDocumentId,
                        onInserted: (versionId)=>void remount(versionId),
                        onJumpToAnchor: (anchor)=>{
                            const target = sections.live.find((s)=>s.node.anchorBookmark === anchor);
                            if (target) navigate(sectionHref(projectId, volumeId, target.node.sectionId));
                        },
                        sectionAnchor: active?.node.anchorBookmark || undefined,
                        unitNoun: "section"
                    })
            },
            unitNoun: "section"
        })
    });
}
